import { Templates } from '@hellohair/types';
import { Box, MenuItem, Typography } from '@mui/material';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AddRowButton,
  CollapsibleRow,
  FieldGroup,
  NextQuestionSelect,
  RepeatableRow,
  RowSummary,
  StepCheckbox,
  StepField,
  StepSelect,
} from '../_shared/fields';
import { appendTo, removeAt, replaceAt } from '../_shared/stepData';
import { StepDialog, StepEditorProps } from '../_shared/StepDialog';
import { useStepDraft } from '../_shared/useStepDraft';

const MultiSelectAnswer: FC<StepEditorProps> = ({ survey, index, onClose }) => {
  const { t } = useTranslation();
  // The renderer falls back to legacy per-option routing while both sentiment
  // followers are null, so materialise them the moment the step is opened.
  const { draft, set, save, dirty } = useStepDraft<Templates.StepDataMulti>(survey, index, (step) => ({
    ...step,
    positiveFollowerId: step.positiveFollowerId ?? -1,
    negativeFollowerId: step.negativeFollowerId ?? -1,
  }));
  const options = draft.options ?? [];
  // Only one answer is unfolded at a time; a freshly added one opens itself.
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const updateOption = (i: number, patch: Partial<Templates.MultiImageOption>) =>
    set('options', replaceAt(options, i, patch) as Templates.MultiOption[]);

  /*
   * `options` stays a flat array - the renderer resolves a submitted value with
   * `options.find(o => o.option === value)`, so a sub-answer has to remain a
   * first-class entry. Nesting is expressed by `parentOption` naming the
   * parent's label, and this editor just presents that as a tree.
   */
  /*eslint-disable-next-line @typescript-eslint/no-explicit-any*/
  const labels = new Set(options.map((o: any) => o.option));
  /*eslint-disable-next-line @typescript-eslint/no-explicit-any*/
  const isChild = (o: any) => !!o.parentOption && labels.has(o.parentOption);
  /*eslint-disable-next-line @typescript-eslint/no-explicit-any*/
  const rootIndexes = options.map((o: any, i: number) => (isChild(o) ? -1 : i)).filter((i: number) => i >= 0);
  const childIndexes = (label: string) =>
    options
      /*eslint-disable-next-line @typescript-eslint/no-explicit-any*/
      .map((o: any, i: number) => (isChild(o) && o.parentOption === label ? i : -1))
      .filter((i: number) => i >= 0);

  /**
   * Renaming a parent has to re-point its children in the same update, or they
   * would orphan and the survey would render them as top-level answers.
   */
  const renameOption = (i: number, label: string) => {
    /*eslint-disable-next-line @typescript-eslint/no-explicit-any*/
    const previous = (options[i] as any)?.option;
    set(
      'options',
      /*eslint-disable-next-line @typescript-eslint/no-explicit-any*/
      options.map((option: any, index: number) => {
        if (index === i) return { ...option, option: label };
        if (previous && option.parentOption === previous) return { ...option, parentOption: label };
        return option;
      }) as Templates.MultiOption[]
    );
  };

  const addChild = (parentLabel: string) => {
    set(
      'options',
      appendTo(options, {
        type: 'SingleSelectOptionText',
        option: '',
        sentiment: Templates.OptionSentiment.Positive,
        parentOption: parentLabel,
      } as Templates.MultiOption)
    );
  };

  /** Removing a parent takes its sub-answers with it. */
  const removeWithChildren = (i: number) => {
    /*eslint-disable-next-line @typescript-eslint/no-explicit-any*/
    const label = (options[i] as any)?.option;
    const doomed = new Set([i, ...(label ? childIndexes(label) : [])]);
    set('options', options.filter((_option, index) => !doomed.has(index)) as Templates.MultiOption[]);
    setOpenIndex(null);
  };

  const addOption = () => {
    set(
      'options',
      appendTo(options, {
        type: 'SingleSelectOptionText',
        option: '',
        sentiment: Templates.OptionSentiment.Positive,
        exclusive: false,
      } as Templates.MultiOption)
    );
    setOpenIndex(options.length);
  };

  return (
    <StepDialog
      survey={survey}
      index={index}
      onSave={save}
      onClose={onClose}
      dirty={dirty}
      edit={
        <>
          <StepField
            label={t('editor.field.question')}
            value={draft.question}
            onChange={(value) => set('question', value)}
            multiline
          />

          <FieldGroup title={t('editor.section.answers')} hint={t('editor.hint.exclusive')}>
            <Typography variant="body2" sx={{ color: 'text.secondary', marginBottom: '4px' }}>
              {t('editor.hint.nesting')}
            </Typography>
            {rootIndexes.map((i: number, position: number) => {
              /*eslint-disable-next-line @typescript-eslint/no-explicit-any*/
              const option = options[i] as any;
              const children = childIndexes(option.option);
              // A parent is never selectable, so sentiment and exclusivity do
              // not apply to it - hide them rather than persist dead fields.
              const isParent = children.length > 0;
              return (
                <CollapsibleRow
                  key={i}
                  expanded={openIndex === i}
                  onToggle={(expanded) => setOpenIndex(expanded ? i : null)}
                  removeLabel={isParent ? t('editor.action.removeAnswerWithSubs') : t('editor.action.removeAnswer')}
                  onRemove={() => removeWithChildren(i)}
                  summary={
                    <RowSummary
                      badge={t('editor.answerNumber', { number: position + 1 })}
                      title={option.option}
                      note={[
                        isParent
                          ? t('editor.field.subAnswers') + ' · ' + children.length
                          : option.sentiment === Templates.OptionSentiment.Negative
                            ? t('editor.sentiment.negative')
                            : t('editor.sentiment.positive'),
                        !isParent && option.exclusive ? t('editor.field.exclusive') : undefined,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    />
                  }
                >
                  <StepField
                    label={t('editor.field.answer')}
                    value={option.option}
                    onChange={(value) => renameOption(i, value)}
                  />
                  {!isParent && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: '140px' }}>
                        <StepSelect
                          label={t('editor.field.sentiment')}
                          value={option.sentiment ?? Templates.OptionSentiment.Positive}
                          onChange={(value) => updateOption(i, { sentiment: value as Templates.OptionSentiment })}
                        >
                          <MenuItem value={Templates.OptionSentiment.Positive}>
                            {t('editor.sentiment.positive')}
                          </MenuItem>
                          <MenuItem value={Templates.OptionSentiment.Negative}>
                            {t('editor.sentiment.negative')}
                          </MenuItem>
                        </StepSelect>
                      </Box>
                      <StepCheckbox
                        label={t('editor.field.exclusive')}
                        checked={!!option.exclusive}
                        onChange={(checked) => updateOption(i, { exclusive: checked })}
                      />
                    </Box>
                  )}

                  <FieldGroup title={t('editor.field.subAnswers')}>
                    {children.map((j: number) => {
                      /*eslint-disable-next-line @typescript-eslint/no-explicit-any*/
                      const child = options[j] as any;
                      return (
                        <RepeatableRow
                          key={j}
                          removeLabel={t('editor.action.removeSubAnswer')}
                          onRemove={() => set('options', removeAt(options, j))}
                        >
                          <Box sx={{ flexGrow: 1, minWidth: '160px' }}>
                            <StepField
                              label={t('editor.field.answer')}
                              value={child.option}
                              onChange={(value) => updateOption(j, { option: value })}
                              // A parent's selected children are joined with "/" in the
                              // answer, so a slash in the text cannot be read back.
                              helperText={
                                (child.option ?? '').includes('/') ? t('editor.hint.subAnswerSlash') : undefined
                              }
                            />
                          </Box>
                          <Box sx={{ width: '140px' }}>
                            <StepSelect
                              label={t('editor.field.sentiment')}
                              value={child.sentiment ?? Templates.OptionSentiment.Positive}
                              onChange={(value) => updateOption(j, { sentiment: value as Templates.OptionSentiment })}
                            >
                              <MenuItem value={Templates.OptionSentiment.Positive}>
                                {t('editor.sentiment.positive')}
                              </MenuItem>
                              <MenuItem value={Templates.OptionSentiment.Negative}>
                                {t('editor.sentiment.negative')}
                              </MenuItem>
                            </StepSelect>
                          </Box>
                        </RepeatableRow>
                      );
                    })}
                    {/* `parentOption` points at the parent's label, so an unnamed
                        parent would silently produce a top-level answer. */}
                    {option.option ? (
                      <AddRowButton label={t('editor.add.subAnswer')} onClick={() => addChild(option.option)} />
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {t('editor.hint.nameBeforeSubAnswers')}
                      </Typography>
                    )}
                  </FieldGroup>
                </CollapsibleRow>
              );
            })}
            <AddRowButton label={t('editor.add.answer')} onClick={addOption} />
          </FieldGroup>
        </>
      }
      logic={
        <>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('editor.hint.sentimentLogic')}
          </Typography>
          <NextQuestionSelect
            label={t('editor.field.nextQuestionPositive')}
            steps={survey.steps}
            value={draft.positiveFollowerId}
            onChange={(followerId) => set('positiveFollowerId', followerId)}
          />
          <NextQuestionSelect
            label={t('editor.field.nextQuestionNegative')}
            steps={survey.steps}
            value={draft.negativeFollowerId}
            onChange={(followerId) => set('negativeFollowerId', followerId)}
          />
        </>
      }
      settings={
        <StepField label={t('editor.field.buttonLabel')} value={draft.next} onChange={(value) => set('next', value)} />
      }
    />
  );
};

export default MultiSelectAnswer;
